import React, { useState } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Pie } from "react-chartjs-2";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

// Register necessary components
ChartJS.register(ArcElement, ChartTooltip, Legend);

const KotakEducationLoanEmiCal = () => {
  const [loanAmount, setLoanAmount] = useState(5000000);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTenure, setLoanTenure] = useState(20);
  const [monthlyEMI, setMonthlyEMI] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [activeYear, setActiveYear] = useState(null);

  const calculateEMI = () => {
    const principal = loanAmount;
    const annualInterest = interestRate / 100;
    const monthlyInterest = annualInterest / 12;
    const tenureInMonths = loanTenure * 12;

    const emi =
      (principal *
        monthlyInterest *
        Math.pow(1 + monthlyInterest, tenureInMonths)) /
      (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);

    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    setMonthlyEMI(emi.toFixed(2));
    setTotalInterest(totalInterest.toFixed(2));
    setTotalAmount(totalAmount.toFixed(2));

    const schedule = [];
    let balance = principal;
    // let year = new Date().getFullYear();
    let currentYear = new Date().getFullYear();

    let currentMonth = 6;

    for (let i = 0; i < tenureInMonths; i++) {
      const interestForMonth = balance * monthlyInterest;
      const principalForMonth = emi - interestForMonth;
      balance -= principalForMonth;

      schedule.push({
        // month: i + 1,
        // year: year,
        month: ((currentMonth + i) % 12) + 1, // Ensure month is in range 1-12
        year: currentYear + Math.floor((currentMonth + i) / 12),
        principalPaid: principalForMonth,
        interestPaid: interestForMonth,
        totalPayment: emi,
        balance: balance,
      });

      // if ((i + 1) % 12 === 0) {
      //   year++;
      // }
    }

    setAmortizationSchedule(schedule);
  };

  const handleLoanAmountChange = (e) => {
    const value = e.target.value;
    setLoanAmount(value === "" ? 0 : parseInt(value));
  };

  const handleInterestRateChange = (e) => {
    const value = e.target.value;
    setInterestRate(value === "" ? 0 : parseFloat(value));
  };

  const handleLoanTenureChange = (e) => {
    const value = e.target.value;
    setLoanTenure(value === "" ? 0 : parseInt(value));
  };

  const handleYearToggle = (year) => {
    setActiveYear(activeYear === year ? null : year);
  };

  const data = {
    labels: ["Principal Amount", "Total Interest"],
    datasets: [
      {
        label: "EMI Breakdown",
        data: [loanAmount, totalInterest],
        backgroundColor: ["#36A2EB", "#FF6384"],
      },
    ],
  };
  const categories = [
    { id: 1, name: "Category 1", link: "/" },
    { id: 2, name: "Category 2", link: "/" },
    { id: 3, name: "Category 3", link: "/" },
    { id: 4, name: "Category 4", link: "/" },
    // Add more categories as needed
  ];
  function Icon({ id, open }) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className={`${
          id === open ? "rotate-180" : ""
        } h-5 w-5 transition-transform`}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  }

  const [activeIndex, setActiveIndex] = useState(0);

  const faqs = [
    {
        "question": "What does this EMI calculator do?",
        "answer": "It calculates your monthly repayment obligations by considering your loan amount, interest rate, tenure, and moratorium period. This helps you make an informed decision."
      },
      {
        "question": "Is moratorium period included in this calculation?",
        "answer": "Yes. You can factor in your study duration and the grace period so that you get an accurate EMI post-completion of your course."
      },
      {
        "question": "How accurate are the EMI results?",
        "answer": "The results use a standard financial formula. While actual disbursed EMIs may vary slightly due to daily interest calculation, this estimate is precise for planning purposes."
      },
      {
        "question": "Can I change values and compare scenarios?",
        "answer": "Absolutely. You can tweak loan amounts, interest rates, or tenures multiple times and instantly compare how it affects your EMI."
      },
      {
        "question": "What interest rate should I use for Kotak loans?",
        "answer": "Use Kotak’s latest published education loan interest rate, which generally ranges from 10.75%–12.5%, depending on your profile and collateral."
      },
      {
        "question": "Is this EMI fixed for the entire tenure?",
        "answer": "It depends. If you choose a fixed-rate loan, your EMI remains constant. If you go for a floating rate, it may vary based on market conditions."
      },
      {
        "question": "Does this tool account for prepayment options?",
        "answer": "You can use the calculator to simulate post-prepayment EMIs. Kotak generally allows partial or full prepayment with minimal charges."
      },
      {
        "question": "Can co-applicants also use this calculator?",
        "answer": "Yes. Since repayment is typically a joint responsibility, co-applicants should also be aware of the EMI figures."
      },
      {
        "question": "What happens during the moratorium period?",
        "answer": "Usually, you are not required to pay EMI, but interest keeps accumulating. The final principal after moratorium includes this accumulated interest."
      },
      {
        "question": "Will tax savings appear in the EMI result?",
        "answer": "No. However, under Section 80E of the Income Tax Act, interest paid on education loans is tax-deductible for up to 8 years."
      },
      {
        "question": "Is there a minimum and maximum loan amount?",
        "answer": "Kotak Mahindra typically offers loans ranging from ₹50,000 to ₹20 lakhs depending on course and location. EMI is calculated accordingly."
      },
      {
        "question": "How does this help students planning foreign studies?",
        "answer": "It lets you calculate monthly repayments for loans covering foreign university fees, living expenses, insurance, and travel costs."
      },
      {
        "question": "Can I download or save the EMI results?",
        "answer": "While the calculator is online, you can manually note down the EMI, interest, and amortisation values for your records or share them with guardians."
      },
      {
        "question": "Is this tool safe and private to use?",
        "answer": "Yes. There is no login, data entry, or personal information involved. Your inputs are never stored or shared."
      },
      {
        "question": "What’s the advantage of using TheFinMaster’s calculator over others?",
        "answer": "It’s highly detailed, accurate, mobile-friendly, and specially tailored for Indian bank education loans—making it a student’s ideal financial companion."
      }
  ];
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };
  return (
    <>
      <Helmet>
           <title>Kotak Education Loan EMI Calculator - Smart EMI Planning Tool</title>
          <meta  name='description' content='Calculate Kotak education loan EMI instantly with our easy-to-use calculator. Plan your education loan repayment efficiently with accurate results.'/>
          <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/kotak-education-loan-emi-calculator" />
          <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
          <meta name="robots" content="max-video-preview:-1" />
        </Helmet>
      <FAQSchema faqs={faqs} />
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <Link
              to="/financial-tools-calculators"
              className="text-blue-600 hover:text-blue-800"
            >
              Financial Tools Calculators
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500 ">
              Kotak Education Loan EMI Calculator
            </span>
          </li>
        </ol>
      </nav> */}
         <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
      <div className="py-4 ">
        <div className="container mx-auto p-4 shadow-xl rounded-md ">
          <h1 className="text-2xl font-bold mb-4">
            Kotak Education Loan EMI Calculator
          </h1>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Amount
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    value={loanAmount}
                    onChange={handleLoanAmountChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">
                    ₹ {loanAmount.toLocaleString()}
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100000000"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Rate of Interest (P.A.)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={interestRate}
                    onChange={handleInterestRateChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{interestRate} %</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="15"
                  step="0.1"
                  value={interestRate}
                  onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold">
                  Loan Tenure (Years)
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    min="0"
                    max="30"
                    value={loanTenure}
                    onChange={handleLoanTenureChange}
                    className="w-1/2 p-2 border rounded mr-2"
                  />
                  <span className="text-gray-700">{loanTenure} Yr</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="30"
                  value={loanTenure}
                  onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                  className="w-full mt-2"
                />
              </div>
              <button
                onClick={calculateEMI}
                className="bg-blue-500 text-white px-4 py-2 rounded"
              >
                Calculate
              </button>
            </div>
            <div>
              <div className="mb-4">
                <h2 className="text-xl font-bold">Results</h2>
                <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
                <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
                <p>
                  Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}
                </p>
                <p>
                  Total Amount (Principal + Interest): ₹{" "}
                  {parseFloat(totalAmount).toLocaleString()}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold">Breakdown</h2>
                <div className="flex justify-center">
                  <Pie
                    data={data}
                    options={{ maintainAspectRatio: false }}
                    width={300}
                    height={300}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
            {amortizationSchedule.length > 0 && (
              <div className="flex flex-col space-y-4">
                {Array.from(
                  new Set(amortizationSchedule.map((item) => item.year))
                ).map((year, idx) => (
                  <div key={idx} className="mb-4 border rounded-md shadow-md">
                    <div
                      className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${
                        activeYear === year ? "bg-blue-100" : ""
                      }`}
                      onClick={() => handleYearToggle(year)}
                    >
                      <span>{year}</span>
                      <span>{activeYear === year ? "-" : "+"}</span>
                    </div>
                    {activeYear === year && (
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-md shadow-md text-center">
                          <thead>
                            <tr>
                              <th className="py-2 px-4 border-b">Month</th>
                              <th className="py-2 px-4 border-b">
                                Principal Paid
                              </th>
                              <th className="py-2 px-4 border-b">
                                Interest Charged
                              </th>
                              <th className="py-2 px-4 border-b">
                                Total Payment
                              </th>
                              <th className="py-2 px-4 border-b">Balance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {amortizationSchedule
                              .filter((item) => item.year === year)
                              .map((item, idx) => (
                                <tr key={idx}>
                                  <td className="py-2 px-4 border-b">
                                    {new Date(0, item.month - 1).toLocaleString(
                                      "default",
                                      { month: "short" }
                                    )}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.principalPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.interestPaid
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹{" "}
                                    {item.totalPayment
                                      .toFixed(2)
                                      .toLocaleString()}
                                  </td>
                                  <td className="py-2 px-4 border-b">
                                    ₹ {item.balance.toFixed(2).toLocaleString()}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6">
        <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        Plan Your Kotak Education Loan Smartly with Our EMI Calculator
      </h2>
      <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
        Pursuing higher education in India or abroad can be an expensive affair, and a structured education loan from Kotak Mahindra Bank can help students chase their dreams without financial stress. Whether it's tuition, travel, accommodation, or exam fees, Kotak covers the broad spectrum of education-related expenses. But equally important is knowing how you’ll repay it—and that’s where TheFinMaster’s Kotak Education Loan EMI Calculator steps in.
        It gives you instant EMI figures, flexible tenure options, moratorium simulation, and an easy-to-read amortisation schedule so that you can plan every rupee with confidence. Whether you're studying engineering in India or pursuing an MBA in the US, this calculator is your first step to financial clarity.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
        What is an Education Loan EMI?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        An Education Loan EMI is a fixed monthly amount you pay towards repaying your student loan after the moratorium period ends. The EMI includes both the interest and the principal components. During the moratorium (study period + grace period), some loans may require only interest payments, while others allow full deferment.
        With Kotak Mahindra Bank, understanding your EMI structure ensures you’re not caught off guard when repayments begin. This foresight lets students and co-applicants prepare their post-education financial journey well in advance.
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         What is the Kotak Education Loan EMI Calculator?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        The Kotak Mahindra Education Loan EMI Calculator is a free, easy-to-use tool that allows you to forecast your repayment obligations in seconds. Simply enter the loan amount, interest rate, tenure, and moratorium period, and the tool will calculate your monthly EMI, total interest payable, and total amount to be repaid.
        Features Include:
        <ul className="list-disc pl-6 mt-2">
          <li>Real-time calculations and breakdowns</li>
          <li>Moratorium inclusion simulation</li>
          <li>Amortisation chart with year-wise breakup</li>
          <li>Clear visual representation of principal vs interest</li>
          <li>Data privacy with no login required</li>
          <li>Works smoothly on mobile and desktop</li>
        </ul>
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         Why Use TheFinMaster’s Kotak EMI Calculator?
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <ul className="list-disc pl-6 mt-2">
          <li><strong>Accuracy</strong> – Instant and precise EMI figures</li>
          <li><strong>Planning</strong> – Helps create a repayment plan well in advance</li>
          <li><strong>Comparisons</strong> – Lets you compare various tenure and interest options</li>
          <li><strong>Customisation</strong> – Flexible inputs for moratorium and partial prepayments</li>
          <li><strong>Transparency</strong> – Know the full cost of borrowing including interest</li>
          <li><strong>Accessibility</strong> – Works 24/7, no registration needed, and completely ad-free</li>
        </ul>
      </p>
    </div>
    <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         How the EMI Calculator Works
      </h2>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        EMI Formula Used:
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        EMI = [P × r × (1 + r)^N] / [(1 + r)^N – 1]
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        Where:
        <ul className="list-disc pl-6 mt-2">
          <li>P = Total loan amount (with interest if moratorium is added)</li>
          <li>r = Monthly interest rate (Annual rate ÷ 12 ÷ 100)</li>
          <li>N = Number of months in loan tenure (excluding moratorium)</li>
        </ul>
      </p>
      <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
         <strong>Example:</strong><br />
        Loan Amount: ₹12,00,000<br />
        Interest Rate: 11%<br />
        Tenure: 7 Years (including 1 year moratorium) → Repayment tenure = 72 months<br />
        EMI = ₹22,562<br />
        Total Repayment = ₹16,24,464<br />
        Total Interest = ₹4,24,464
      </p>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         Sample Amortisation Table (First 12 Months After Moratorium)
      </h2>
      <div className="mt-6">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2 text-left">Month</th>
                <th className="border px-4 py-2 text-left">EMI (₹)</th>
                <th className="border px-4 py-2 text-left">Interest (₹)</th>
                <th className="border px-4 py-2 text-left">Principal (₹)</th>
                <th className="border px-4 py-2 text-left">Outstanding Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["1", "22,562", "11,000", "11,562", "11,88,438"],
                ["2", "22,562", "10,897", "11,665", "11,76,773"],
                ["3", "22,562", "10,774", "11,788", "11,64,985"],
                ["4", "22,562", "10,641", "11,921", "11,53,064"],
                ["5", "22,562", "10,507", "12,055", "11,41,009"],
                ["6", "22,562", "10,371", "12,191", "11,28,818"],
                ["7", "22,562", "10,234", "12,328", "11,16,490"],
                ["8", "22,562", "10,095", "12,467", "11,04,023"],
                ["9", "22,562", "9,954", "12,608", "10,91,415"],
                ["10", "22,562", "9,812", "12,750", "10,78,665"],
                ["11", "22,562", "9,669", "12,893", "10,65,772"],
                ["12", "22,562", "9,524", "13,038", "10,52,734"]
              ].map(([month, emi, interest, principal, balance], index) => (
                <tr key={index}>
                  <td className="border px-4 py-2">{month}</td>
                  <td className="border px-4 py-2">{emi}</td>
                  <td className="border px-4 py-2">{interest}</td>
                  <td className="border px-4 py-2">{principal}</td>
                  <td className="border px-4 py-2">{balance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-4 mt-4">
          {[
            ["1", "22,562", "11,000", "11,562", "11,88,438"],
            ["2", "22,562", "10,897", "11,665", "11,76,773"],
            ["3", "22,562", "10,774", "11,788", "11,64,985"],
            ["4", "22,562", "10,641", "11,921", "11,53,064"],
            ["5", "22,562", "10,507", "12,055", "11,41,009"],
            ["6", "22,562", "10,371", "12,191", "11,28,818"],
            ["7", "22,562", "10,234", "12,328", "11,16,490"],
            ["8", "22,562", "10,095", "12,467", "11,04,023"],
            ["9", "22,562", "9,954", "12,608", "10,91,415"],
            ["10", "22,562", "9,812", "12,750", "10,78,665"],
            ["11", "22,562", "9,669", "12,893", "10,65,772"],
            ["12", "22,562", "9,524", "13,038", "10,52,734"]
          ].map(([month, emi, interest, principal, balance], index) => (
            <div key={index} className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border">
              <div><span className="font-semibold">Month:</span> {month}</div>
              <div><span className="font-semibold">EMI (₹):</span> {emi}</div>
              <div><span className="font-semibold">Interest (₹):</span> {interest}</div>
              <div><span className="font-semibold">Principal (₹):</span> {principal}</div>
              <div><span className="font-semibold">Outstanding Balance (₹):</span> {balance}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
    <div className="container mx-auto py-4">
      <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
         Key Factors That Influence EMI
      </h2>
      <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
        <li><strong>Loan Amount</strong> – Higher principal means higher EMI and interest, so borrow only what’s necessary.</li>
        <li><strong>Interest Rate</strong> – Kotak’s rates are competitive, but even a 0.5% difference impacts the total cost.</li>
        <li><strong>Tenure</strong> – Longer tenure reduces monthly EMI but increases interest outgo.</li>
        <li><strong>Moratorium</strong> – Longer moratorium means interest accumulates before EMI begins, raising the final EMI.</li>
        <li><strong>Prepayment Options</strong> – Use Kotak’s partial prepayment feature to reduce EMI or shorten tenure.</li>
        <li><strong>Type of Interest Rate</strong> – Fixed gives predictability. Floating varies over time and affects EMI.</li>
        <li><strong>Collateral Terms</strong> – For higher loans, property or financial assets may be required as security.</li>
      </ul>
    </div>
  </div>
          <div className="w-full md:w-1/3 p-4">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
                <h2 className="text-lg font-semibold ">Popular Categories</h2>
              </div>
              <div className="p-2 ">
                <ul>
                  <Link to="/financial-tools-calculators/swp-calculator">
                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">
                      SWP Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/mutul-fund-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Mutual Fund Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/sukanya-samriddhi-yojana-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Samriddhi Yojana Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/fd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      FD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/rd-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      RD Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nps-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NPS Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/hra-exemption-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      HRA Exemption Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/retirement-planning-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Retirement Planning Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      EMI Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/simple-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Simple Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/compound-interest-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Compound Interest Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/nsc-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      NSC Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/step-up-sip-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Step Up Sip Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/income-tax-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Income Tax Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/home-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Home Loan Emi Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/car-loan-emi-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Car Loan Emi Calculator
                    </li>
                  </Link>
                  {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
                {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
            <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
              <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                <h2 className="text-lg font-semibold">More Categories</h2>
              </div>
              <div className="p-2">
                <ul>
                  <Link to="/financial-tools-calculators/gratuity-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Gratuity Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/APY-calculator">
                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">
                      APY Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/cagr-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      CAGR Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/gst-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      GST Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/flat-vs-reducing-rate-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Flat Vs Reducing Rate Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/equity-margin-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Equity Margin Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/inflation-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Inflation Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/salary-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Salary Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/brokerage-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      Brokerage Calculator
                    </li>
                  </Link>
                  <Link to="/financial-tools-calculators/tds-calculator">
                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">
                      TDS Calculator
                    </li>
                  </Link>
                  {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
        <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default KotakEducationLoanEmiCal;
